// PDF-Rechnung Generierung via jsPDF (§11 UStG Layout)

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { type Rechnung, type Aussteller } from '../types';
import { formatEuroDisplay, formatDateDE } from './rechnungBuilder';
import { type EuroCents } from '../../../types/branded';

// ── Farben + Konstanten ───────────────────────────────────────────────────

const BLUE   = [0, 63, 127]  as const;   // AUVA-Blau
const GRAY   = [100, 100, 100] as const;
const LGRAY  = [230, 230, 230] as const;
const BLACK  = [30, 30, 30]  as const;

const MARGIN_L = 20;
const MARGIN_R = 20;
const PAGE_W   = 210;

// ── PDF generieren ────────────────────────────────────────────────────────

export function generateRechnungPDF(rechnung: Rechnung, aussteller: Aussteller): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  let y = MARGIN_L;

  // ── Kopfzeile: Absender ───────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const absender = [
    aussteller.titelVor, aussteller.name, aussteller.titelNach,
  ].filter(Boolean).join(' ');
  doc.text(
    `${absender} · ${aussteller.strasse} · ${aussteller.plz} ${aussteller.ort}`,
    MARGIN_L, y
  );

  // ── Logo/Name oben rechts ─────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setTextColor(...BLUE);
  doc.setFont('helvetica', 'bold');
  doc.text(absender, PAGE_W - MARGIN_R, y, { align: 'right' });
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);
  doc.text('Arbeitsmedizin', PAGE_W - MARGIN_R, y, { align: 'right' });

  y += 12;

  // ── Empfänger-Adressfeld ──────────────────────────────────────────────
  const e = rechnung.empfaenger;
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'bold');
  doc.text(e.firma, MARGIN_L, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(e.strasse, MARGIN_L, y);
  y += 5;
  doc.text(`${e.plz} ${e.ort}`, MARGIN_L, y);
  y += 5;
  if (e.uid !== undefined && e.uid !== '') {
    doc.setTextColor(...GRAY);
    doc.setFontSize(9);
    doc.text(`UID: ${e.uid}`, MARGIN_L, y);
    y += 4;
  }

  // ── Rechnungsinfos oben rechts ────────────────────────────────────────
  const infoY = MARGIN_L + 17;
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const infos: [string, string][] = [
    ['Rechnungsdatum:', formatDateDE(rechnung.datum)],
    ['Fällig bis:', formatDateDE(rechnung.faelligAm)],
    ['Rechnungs-Nr.:', rechnung.rechnungsNr],
  ];
  if (rechnung.leistungszeitraum !== undefined && rechnung.leistungszeitraum !== '') {
    infos.push(['Leistungszeitraum:', rechnung.leistungszeitraum]);
  }
  infos.forEach(([label, val], i) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, PAGE_W - MARGIN_R - 60, infoY + i * 5);
    doc.setFont('helvetica', 'normal');
    doc.text(val, PAGE_W - MARGIN_R, infoY + i * 5, { align: 'right' });
  });

  y += 10;

  // ── Trennlinie ────────────────────────────────────────────────────────
  doc.setDrawColor(...LGRAY);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
  y += 8;

  // ── Titel ─────────────────────────────────────────────────────────────
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLUE);
  doc.text('RECHNUNG', MARGIN_L, y);
  doc.setFontSize(11);
  doc.setTextColor(...GRAY);
  doc.text(rechnung.rechnungsNr, MARGIN_L + 50, y);
  y += 12;

  // ── Leistungstabelle ──────────────────────────────────────────────────
  const tableData = rechnung.positionen.map(p => [
    p.leistungCode,
    p.bezeichnung,
    `${p.menge} ${p.einheit}`,
    formatEuroDisplay(p.einzelpreisNetto),
    p.ustSatz === 0 ? 'stfr.' : `${p.ustSatz}%`,
    formatEuroDisplay(p.gesamtNetto),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Code', 'Leistung', 'Menge', 'Einzelpreis', 'USt', 'Netto']],
    body: tableData,
    margin: { left: MARGIN_L, right: MARGIN_R },
    headStyles: {
      fillColor: BLUE as [number, number, number],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: BLACK as [number, number, number] },
    alternateRowStyles: { fillColor: [248, 250, 252] as [number, number, number] },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 70 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 15, halign: 'center' },
      5: { cellWidth: 20, halign: 'right' },
    },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // ── Summenblock ───────────────────────────────────────────────────────
  const sumX = PAGE_W - MARGIN_R - 80;
  const valX = PAGE_W - MARGIN_R;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);

  // USt-Aufschlüsselung nach Steuersätzen
  const ustGruppen = new Map<number, EuroCents>();
  for (const p of rechnung.positionen) {
    const cur = ustGruppen.get(p.ustSatz) ?? 0 as EuroCents;
    ustGruppen.set(p.ustSatz, (cur + p.gesamtUst) as EuroCents);
  }

  doc.text('Summe netto:', sumX, y);
  doc.text(formatEuroDisplay(rechnung.summeNetto), valX, y, { align: 'right' });
  y += 5;

  for (const [satz, betrag] of ustGruppen.entries()) {
    if (satz === 0) {
      doc.text('Steuerfreit (unecht):', sumX, y);
      doc.text(formatEuroDisplay(betrag), valX, y, { align: 'right' });
    } else {
      doc.text(`USt ${satz}%:`, sumX, y);
      doc.text(formatEuroDisplay(betrag), valX, y, { align: 'right' });
    }
    y += 5;
  }

  // Linie vor Brutto
  doc.setDrawColor(...LGRAY);
  doc.line(sumX, y - 1, valX, y - 1);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLUE);
  doc.text('GESAMTBETRAG:', sumX, y + 4);
  doc.text(formatEuroDisplay(rechnung.summeBrutto), valX, y + 4, { align: 'right' });

  y += 14;

  // ── Zahlungsbedingungen ───────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);
  doc.text(rechnung.zahlungsbedingungen, MARGIN_L, y);
  y += 5;

  if (rechnung.anmerkungen !== undefined && rechnung.anmerkungen !== '') {
    doc.text(rechnung.anmerkungen, MARGIN_L, y);
    y += 5;
  }

  // ── Bankverbindung ────────────────────────────────────────────────────
  y += 5;
  doc.setDrawColor(...LGRAY);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
  y += 5;

  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  const bankInfo = [
    `IBAN: ${aussteller.iban}`,
    `BIC: ${aussteller.bic}`,
    `Bank: ${aussteller.bank}`,
  ].join('   |   ');
  doc.text(bankInfo, MARGIN_L, y);
  y += 4;

  const uidInfo = [
    `UID: ${aussteller.uid}`,
    aussteller.steuernummer !== undefined ? `StNr: ${aussteller.steuernummer}` : '',
    aussteller.email !== undefined ? `E-Mail: ${aussteller.email}` : '',
  ].filter(Boolean).join('   |   ');
  doc.text(uidInfo, MARGIN_L, y);

  // ── Seitenzahl ────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.text('Seite 1 / 1', PAGE_W / 2, 290, { align: 'center' });

  return doc.output('blob');
}

// ── Dateiname ─────────────────────────────────────────────────────────────

export function rechnungDateiname(rechnung: Rechnung): string {
  return `Rechnung_${rechnung.rechnungsNr}_${rechnung.datum}.pdf`;
}
